/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { Fragment, useContext, useMemo, useState } from 'react'
import {
    Icon,
    Table,
    TableCell,
    TableHeaderCell,
    TableRow,
} from 'semantic-ui-react'
import { useTranslation } from 'react-i18next'
import { TableVirtuoso } from 'react-virtuoso'
import { UltyMoney } from '@ulty/ulty-ui'
import { UltyModalWrapperContext } from '../../../../Shared/UltyModalWrapper/UltyModalWrapperContext'
import OrderItemsRich from '../OrderItemsRich/OrderItemsRich'
import useUser from '../../../../Shared/UserProvider/useUser'
import { useOrderPickingScan } from '../../OrderScanPicking'
import { FoundItemButton } from '../OrderItemFulfillment/FoundItemButton'
import {
    OrderItemFulfillmentIssueBar,
} from '../OrderItemFulfillment/OrderItemFulfillmentIssueBar/OrderItemFulfillmentIssueBar'
import { FulfillmentState } from '../OrderItemFulfillment/FulfillmentState'
import { FulfillmentIssueLabel } from '../OrderItemFulfillment/FulfillmentIssueLabel'
import { OrderItemQuantity } from '../OrderItemQuantity'
import { useDeviceMediaQuery } from '../../../../Shared/UseDeviceMediaQuery/useDeviceMediaQuery'
import useMediaQuery from 'beautiful-react-hooks/useMediaQuery'


const buildModifiers = (modifiers, prefix = 0) =>
  modifiers.map((m, idx) => {
      const Pre = m.modifiers?.length > 0 ? () => buildModifiers(m.modifiers, prefix + 1) : () => null
      return (
        <Fragment key={`${m.name}-(${m.quantity}x)-${idx}`}>
            <div style={{paddingLeft: `${prefix * 10}px`}}>{`- ${m.name} (${m.quantity}x)`}</div>
            <Pre/>
        </Fragment>
      )
  })

const OrderItemCells = ({order, orderItem, relatedReplacements, onOrderItemDetailsPreview, currentQuantity}) => {
    const {user} = useUser()
    const [quantity, setQuantity] = useState(currentQuantity ?? orderItem.quantity)
    const {scannedItemsMap} = useOrderPickingScan()
    const [t] = useTranslation()

    if (currentQuantity !== undefined && currentQuantity !== quantity) {
        setQuantity(currentQuantity)
    }

    return (
      <>
          <TableCell css={css`
              display: flex;
              flex-direction: column;
              font-style: italic;

              a {
                  color: rgb(0, 0, 0);
              }

              a:hover {
                  color: #1e70bf;
              }
          `}>
              <a
                href="#"
                onClick={onOrderItemDetailsPreview}
                style={{textDecoration: parseInt(quantity) === 0 ? 'line-through' : 'none'}}
              >{orderItem.name}</a>
              {orderItem.modifiers && buildModifiers(orderItem.modifiers)}
          </TableCell>

          <TableCell>
              <div css={css`&:has(*) {
                  margin-bottom: .5rem;
              }`}>
                  <FulfillmentState orderItem={orderItem} scanValue={scannedItemsMap.get(orderItem?.id)}/>
                  {orderItem.fulfillmentIssue &&
                    orderItem.fulfillmentIssue !== 'FOUND_ITEM' &&
                    orderItem.customerAckType !== 'AWAITING_ACTION' &&
                    <FulfillmentIssueLabel orderItem={orderItem}/>
                  }
              </div>
              <div css={css`display: flex;
                  flex-direction: column;
                  row-gap: .5rem;`}>
                  {order.isModifiable &&
                    (!orderItem.customerAckType || orderItem.customerAckType === 'AWAITING_ACTION') &&
                    (!orderItem.fulfillmentIssue ||
                      orderItem.fulfillmentIssue === 'PARTIAL_AVAILABILITY' ||
                      orderItem.fulfillmentIssue === 'SUBSTITUTION' ||
                      orderItem.fulfillmentIssue === 'FOUND_ITEM') &&
                    <OrderItemFulfillmentIssueBar
                      order={order}
                      orderItem={orderItem}
                      hasReplacement={relatedReplacements && relatedReplacements.length > 0}
                    />
                  }
                  {!orderItem.pickingMethod &&
                    (!orderItem.customerAckType || orderItem.customerAckType === 'AWAITING_ACTION') &&
                    (!orderItem.fulfillmentIssue || ['OUT_OF_ITEM', 'SUBSTITUTION'].includes(orderItem.fulfillmentIssue)) &&
                    ['RECEIVED', 'ACCEPTED', 'PREPARING'].includes(order.status) &&
                    <FoundItemButton order={order} orderItem={orderItem}/>
                  }
              </div>
          </TableCell>

          <TableCell textAlign="right" className={(orderItem.quantity || 0) > 1 ? 'multi-items' : ''}>
              <OrderItemQuantity
                css={orderItem.quantity !== orderItem.initialQuantity ? css`text-decoration: line-through;` : undefined}
                orderItem={orderItem}
                quantity={orderItem.initialQuantity}
              />
              {orderItem.quantity !== orderItem.initialQuantity && <>
                  <Icon style={{margin: '0.3rem', verticalAlign: 'middle'}} size="small" color="black"
                        name="long arrow alternate right"/>
                  <OrderItemQuantity orderItem={orderItem} quantity={orderItem.quantity}/>
              </>}
          </TableCell>

          <TableCell textAlign="right">
              <UltyMoney
                readonly
                amount={orderItem.unitPrice}
                perUnitOption={orderItem.item && orderItem.item.pricedByUnit?.type !== 'COUNT' ? {
                    value: orderItem.item.pricedByUnit?.unit,
                    text: t(`measurement_unit.symbols.${orderItem.item.pricedByUnit?.unit}`),
                } : {value: user.getCompanyCurrency(), text: ''}}
                currencyCode={user.getCompanyCurrency()}
                locale={user.getCompanyLocale()}
              />
          </TableCell>

          <TableCell textAlign="right">
              {orderItem.totalPrice
                ? <UltyMoney readonly amount={orderItem.totalPrice} currencyCode={user.getCompanyCurrency()}
                             locale={user.getCompanyLocale()}/>
                : '-'
              }
          </TableCell>
      </>
    )
}

const ReplacementOrderItemCells = ({relatedReplacement, onOrderItemDetailsPreview}) => {
    const {user} = useUser()
    const {scannedItemsMap} = useOrderPickingScan()
    const [t] = useTranslation()

    return (
      <>
          <TableCell css={css`
              display: flex;
              flex-direction: column;
              font-style: italic;

              a {
                  color: rgb(0, 0, 0);
              }

              a:hover {
                  color: #1e70bf;
              }
          `}>
              <div css={css`display: flex;`}>
                  <Icon
                    name="level up alternate"
                    rotated="clockwise"
                    css={css`&.icon {
                        align-self: center;
                    }`}
                  />
                  <div css={css`display: flex;
                      flex-direction: column;`}>
                      <a
                        href="#"
                        onClick={onOrderItemDetailsPreview}
                        style={{textDecoration: parseInt(relatedReplacement.quantity) === 0 ? 'line-through' : 'none'}}
                      >{relatedReplacement.name}</a>
                  </div>
              </div>
          </TableCell>

          <TableCell>
              <FulfillmentState orderItem={relatedReplacement} scanValue={scannedItemsMap.get(relatedReplacement.id)}/>
              {relatedReplacement.fulfillmentIssue &&
                relatedReplacement.fulfillmentIssue !== 'FOUND_ITEM' &&
                relatedReplacement.customerAckType !== 'AWAITING_ACTION' &&
                <FulfillmentIssueLabel orderItem={relatedReplacement}/>
              }
          </TableCell>

          <TableCell textAlign="right" className={(relatedReplacement.quantity || 0) > 1 ? 'multi-items' : ''}>
              <OrderItemQuantity orderItem={relatedReplacement} quantity={relatedReplacement.quantity}/>
          </TableCell>

          <TableCell textAlign="right">
              <UltyMoney
                readonly
                amount={relatedReplacement.unitPrice}
                perUnitOption={relatedReplacement.item?.pricedByUnit?.type !== 'COUNT' ? {
                    value: relatedReplacement.item.pricedByUnit?.unit,
                    text: t(`measurement_unit.symbols.${relatedReplacement.item.pricedByUnit?.unit}`),
                } : {value: user.getCompanyCurrency(), text: ''}}
                currencyCode={user.getCompanyCurrency()}
                locale={user.getCompanyLocale()}
              />
          </TableCell>

          <TableCell textAlign="right">
              {relatedReplacement.totalPrice
                ? <UltyMoney readonly amount={relatedReplacement.totalPrice} currencyCode={user.getCompanyCurrency()}
                             locale={user.getCompanyLocale()}/>
                : '-'
              }
          </TableCell>
      </>
    )
}

const DiscountPriceRow = ({discountPrice}) => {
    const [t] = useTranslation()
    const {user} = useUser()

    return (
      <TableRow>
          <TableHeaderCell/>
          <TableHeaderCell/>
          <TableHeaderCell/>
          <TableHeaderCell textAlign="right" colSpan="2" style={{color: 'green', fontWeight: 'bold'}}>
              {t('order.discount')}
              <UltyMoney
                readonly
                amount={discountPrice}
                currencyCode={user.getCompanyCurrency()}
                locale={user.getCompanyLocale()}
              />
          </TableHeaderCell>
      </TableRow>
    )
}

const TotalRow = ({order, sumProducts}) => {
    const [t] = useTranslation()
    const {user} = useUser()

    return (
      <TableRow css={css`
          s, strike {
              text-decoration: none;
              position: relative;
              font-size: .8em;
          }

          s::before, strike::before {
              top: 50%;
              background: red;
              opacity: .7;
              content: '';
              width: 110%;
              position: absolute;
              height: .2em;
              border-radius: .2em;
              left: -5%;
              white-space: nowrap;
              display: block;
              transform: rotate(-15deg);
          }

          s.straight::before, strike.straight::before {
              transform: rotate(0deg);
              left: -1%;
              width: 102%;
          }
      `}>
          <TableHeaderCell>{t('order.total')}</TableHeaderCell>
          <TableHeaderCell/>
          <TableHeaderCell/>
          <TableHeaderCell/>
          <TableHeaderCell textAlign="right" style={{
              color: order.price !== sumProducts ? 'red' : 'black',
              fontWeight: 'bold',
          }}>
              {order.discountPrice && order.discountPrice > 0 ? (
                <div css={css`
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;

                    & > *:not(:last-child) {
                        margin-right: 1rem !important;
                    }
                `}>
                    <s>
                        <UltyMoney
                          readonly
                          amount={order.price}
                          currencyCode={user.getCompanyCurrency()}
                          locale={user.getCompanyLocale()}
                        />
                    </s>
                    <UltyMoney
                      css={css`margin: 0`}
                      readonly
                      amount={order.price - order.discountPrice}
                      currencyCode={user.getCompanyCurrency()}
                      locale={user.getCompanyLocale()}
                    />
                </div>
              ) : (
                <UltyMoney
                  readonly
                  amount={order.price}
                  currencyCode={user.getCompanyCurrency()}
                  locale={user.getCompanyLocale()}
                />
              )}
          </TableHeaderCell>
      </TableRow>
    )
}

const OrderItemsTable = ({order}) => {
    const [t] = useTranslation()
    const {handleUltyModalWrapper} = useContext(UltyModalWrapperContext)
    const { isTablet } = useDeviceMediaQuery()
    const isSmallTablet = useMediaQuery(`(max-width: 900px)`)
    const isBigTablet = useMediaQuery(`(max-width: 1200px)`)

    const orderItemsWithFulfillmentIssue = useMemo(
      () => order.orderItems.filter(oi => oi.fulfillmentIssue && oi.fulfillmentIssue !== 'FOUND_ITEM'),
      [order.orderItems],
    )

    const sumProducts = useMemo(
      () => order.orderItems.reduce((total, oi) => total + oi.quantity * oi.unitPrice, 0),
      [order.orderItems],
    )

    const shouldDisplayDiscountPrice = orderItemsWithFulfillmentIssue.length === 0 && order?.discountPrice > 0

    const defineProductViewModal = (items) => {
        handleUltyModalWrapper(true, {
            title: t('order.product_details'),
            component: <OrderItemsRich order={items} afterSubmit={() => handleUltyModalWrapper(false, null)}/>,
        })
    }

    // Flatten main items and their replacements into a single list so that TableVirtuoso can render one row per entry.
    const flatRows =
        order.orderItems
          .filter(oi => !oi.fulfillmentIssueParentId)
          .flatMap(oi => {
              const replacements = order.orderItems.filter(r => r.fulfillmentIssueParentId === oi.id)
              const oiFulfillmentIssue = orderItemsWithFulfillmentIssue.find(fi => fi.id === oi.id)
              return [
                  {
                      type: 'main',
                      orderItem: oi,
                      relatedReplacements: replacements,
                      currentQuantity: oiFulfillmentIssue?.quantity,
                  },
                  ...replacements.map(r => ({type: 'replacement', orderItem: r})),
              ]
          })

    return (
      <TableVirtuoso
        data={flatRows}
        components={{
            Table: (props) => <Table unstackable size="small" {...props}/>,
        }}
        useWindowScroll
        overscan={800}
        fixedHeaderContent={() => (
          <TableRow>
              <TableHeaderCell width={7}>{t('order.num_products', {count: order.nbProducts})}</TableHeaderCell>
              <TableHeaderCell width={5}>{t('order.fulfillment.state')}</TableHeaderCell>
              <TableHeaderCell textAlign="right" width={1}>{t('order.quantity')}</TableHeaderCell>
              <TableHeaderCell textAlign="right" width={2}>{t('order.order_item.unit_price')}</TableHeaderCell>
              <TableHeaderCell textAlign="right" width={1}>{t('order.order_item.total_price')}</TableHeaderCell>
          </TableRow>
        )}
        fixedFooterContent={() => (
          <>
              {shouldDisplayDiscountPrice && <DiscountPriceRow discountPrice={order.discountPrice}/>}
              <TotalRow order={order} sumProducts={sumProducts}/>
          </>
        )}
        itemContent={(index, row) => {
            if (row.type === 'main') {
                return (
                  <OrderItemCells
                    order={order}
                    orderItem={row.orderItem}
                    relatedReplacements={row.relatedReplacements}
                    onOrderItemDetailsPreview={() => defineProductViewModal(order)}
                    currentQuantity={row.currentQuantity}
                  />
                )
            }
            return (
              <ReplacementOrderItemCells
                relatedReplacement={row.orderItem}
                onOrderItemDetailsPreview={() => defineProductViewModal(order)}
              />
            )
        }}
      />
    )
}

export default OrderItemsTable
